<template>
    <div class="step-rules-config shadow-off pt-2 pb-0 pl-0 pr-0" data-testid="step-rules-config__view">
        <v-row>
            <v-col cols="12" class="pb-0">
                <div class="d-flex gap-2">
                
                    <v-spacer></v-spacer>
                    <base-button
                        v-if="rulesdata.length > 0"
                        secondary
                        label="Save Order"
                        class=""
                        :disabled="!isDirty"
                        data-testid="step-rules-config__save-rules-btn"
                        @click="saveOrder"
                    />
                    <base-button
                        primary
                        label="Rule"
                        prepend-icon="mdi-plus"
                        data-testid="step-rules-config__add-rule-btn"
                        @click="$emit('openAddEditRuleDialog')"
                    />
                </div>
            </v-col>

            <v-col cols="12" class="pt-0">
                <h5 class="font-weight-medium mt-3 mb-3">Rules</h5>

                <div
                    v-if="hasValidData(rulesdata)"
                    data-testid="step-rules-config__rules-list"
                    class="d-flex flex-column gap-4"
                >
                    <div
                        v-for="(v, k) in sortedRulesData"
                        :key="v.key || ('m' + v._originalIndex)"
                        :id="'automation-test-' + v._originalIndex"
                        class="step-rules-config__rule-item"
                        draggable="true"
                        data-testid="step-rules-config__rule-item"
                        @dragstart="onDragStart(k)"
                        @dragover.prevent
                        @drop="onDroprule(k)"
                        @dragend="onDragEnd"
                    >
                        <div class="step-rules-config__rule-index">{{ v.number }}</div>

                        <div class="step-rules-config__rule-card radius-small">
                            <!-- Rule header: chips + action icons -->
                            <div class="step-rules-config__rule-header">
                                <img src="/img/icons/tm-icons/DragIcon.svg" alt="Drag Handle" class="step-rules-config__drag-handle" data-testid="step-rules-config__rule-drag-handle"/>

                                <v-chip
                                    v-if="v && v.nature === 'Automated'"
                                    class="automated-chip"
                                    data-testid="step-rules-config__rule-nature-automated"
                                >Automated</v-chip>
                                <v-chip
                                    v-else-if="v && v.nature === 'Suggested'"
                                    class="suggested-chip"
                                    data-testid="step-rules-config__rule-nature-suggested"
                                >Suggested</v-chip>
                                <v-chip
                                    v-else-if="v && v.nature === 'Manual'"
                                    class="manual-chip"
                                    data-testid="step-rules-config__rule-nature-manual"
                                >Manual</v-chip>

                                <v-chip
                                    v-if="v && v.type === '1:1'"
                                    outlined 
                                    class="grey--text text--darken-2 radius-small"
                                    data-testid="step-rules-config__rule-type-1to1"
                                >One-to-One</v-chip>
                                <v-chip
                                    v-else-if="v && v.type === '1:M'"
                                    outlined 
                                    class="grey--text text--darken-2 radius-small"
                                    data-testid="step-rules-config__rule-type-1tom"
                                >One-to-Many</v-chip>
                                <v-chip
                                    v-else-if="v && v.type === 'M:1'"
                                    outlined 
                                    class="grey--text text--darken-2 radius-small"
                                    data-testid="step-rules-config__rule-type-mto1"
                                >Many-to-One</v-chip>
                                <v-chip
                                    v-else-if="v && v.type === 'M:M'"
                                    outlined 
                                    class="grey--text text--darken-2 radius-small"
                                    data-testid="step-rules-config__rule-type-mtom"
                                >Many-to-Many</v-chip>

                                <v-chip
                                    v-if="v && v.matchWithEntity"
                                    outlined 
                                    class="grey--text text--darken-2 radius-small"
                                    data-testid="step-rules-config__rule-match-with"
                                >Dataset {{ v.baseEntity || 'A' }} with {{ v.matchWithEntity }}</v-chip>

                                <v-chip
                                    v-if="v && (v.file1Name || v.file2Name)"
                                     label
                                    color="grey lighten-4"
                                    class="grey--text text--darken-2 radius-small"
                                    data-testid="step-rules-config__rule-filenames-chip"
                                >
                                    <v-icon left x-small>mdi-file-compare</v-icon>
                                    {{ [v.file1Name, v.file2Name].filter(Boolean).join('  ↔  ') }}
                                </v-chip>

                                <v-spacer></v-spacer>

                                <v-tooltip bottom content-class="tooltip-bottom">
                                    <template #activator="{ on }">
                                        <v-btn
                                            icon small
                                            class="mr-1 grey--text"
                                            data-testid="step-rules-config__rule-edit-btn"
                                            v-on="on"
                                            @click="$emit('openAddEditRuleDialog', v, 'edit', v._originalIndex)"
                                        >
                                            <img src="/img/icons/PenEditIcon.svg" alt="Edit" data-testid="step-rules-config__rule-edit-icon"/>
                                        </v-btn>
                                    </template>
                                    <span>Edit rule</span>
                                </v-tooltip>

                                <v-tooltip bottom content-class="tooltip-bottom">
                                    <template #activator="{ on }">
                                        <v-btn
                                            icon small
                                            class="grey--text hover-red"
                                            data-testid="step-rules-config__rule-delete-btn"
                                            v-on="on"
                                            @click="promptDelete(v._originalIndex)"
                                        >
                                            <img src="/img/icons/tm-icons/Trash.svg" alt="Delete" data-testid="step-rules-config__rule-delete-icon"/>
                                        </v-btn>
                                    </template>
                                    <span>Delete rule</span>
                                </v-tooltip>
                            </div>

                            <!-- Rule condition rows -->
                            <ul
                                class="step-rules-config__rule-details"
                                data-testid="step-rules-config__rule-details"
                            >
                                <li
                                    v-for="(vv, kk) in v && v.cols"
                                    :key="kk"
                                    :id="'automation-test-' + kk"
                                    class="step-rules-config__condition-row"
                                    data-testid="step-rules-config__rule-condition"
                                >
                                    <template v-if="vv.type === 'exact'">
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file1Column) || kk }}</span>
                                        <span class="grey--text mr-2">exactly matches</span>
                                        <span class="font-weight-bold grey--text text--darken-4">{{ (vv.file2Column) || vv.matchWith || kk }}</span>
                                    </template>

                                    <template v-else-if="vv.type === 'contains'">
                                        <template v-if="vv.in !== 'first'">
                                            <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file1Column) || kk }}</span>
                                            <span class="grey--text mr-2">contains in</span>
                                            <span class="font-weight-bold grey--text text--darken-4">{{ (vv.file2Column) || vv.matchWith || kk }}</span>
                                        </template>
                                        <template v-else>
                                            <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file2Column) || vv.matchWith || kk }}</span>
                                            <span class="grey--text mr-2">contains in</span>
                                            <span class="font-weight-bold grey--text text--darken-4">{{ (vv.file1Column) || kk }}</span>
                                        </template>
                                    </template>

                                    <template v-else-if="typeof vv.type === 'number'">
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ vv.file1Column || kk }}</span>
                                        <span class="grey--text mr-2">and</span>
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ vv.file2Column || vv.matchWith || kk }}</span>
                                        <template v-if="vv.toleranceUnit === 'days'">
                                          <span class="grey--text">has maximum {{ vv.type }} days difference between each other</span>
                                          <span v-if="vv.aggregationMethod" class="grey--text">, Aggregated by <span class="font-weight-bold grey--text text--darken-4">{{ vv.aggregationMethod }}</span></span>
                                          <span v-if="vv.negative" class="grey--text">, With Opposite Sign Match</span>
                                        </template>
                                        <template v-else-if="vv.type >= 0">
                                          <span class="grey--text">has maximum {{ vv.type }} difference between each other</span>
                                          <span v-if="vv.aggregationMethod" class="grey--text">, Aggregated by <span class="font-weight-bold grey--text text--darken-4">{{ vv.aggregationMethod }}</span></span>
                                          <span v-if="vv.negative" class="grey--text">, With Opposite Sign Match</span>
                                        </template>
                                    </template>

                                    <template v-else-if="vv.type === 'approximate match'">
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file1Column) || kk }}</span>
                                        <span class="grey--text mr-2">approximate match</span>
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file2Column) || vv.matchWith || kk }}</span>
                                        <span class="grey--text">matching ratio %{{ vv.matchratio }}</span>
                                    </template>

                                    <template v-else-if="vv.type === 'begins with'">
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file1Column) || kk }}</span>
                                        <span class="grey--text mr-2">begins with</span>
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file2Column) || vv.matchWith || kk }}</span>
                                    </template>

                                    <template v-else-if="vv.type === 'ends with'">
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file1Column) || kk }}</span>
                                        <span class="grey--text mr-2">ends with</span>
                                        <span class="font-weight-bold grey--text text--darken-4 mr-2">{{ (vv.file2Column) || vv.matchWith || kk }}</span>
                                    </template>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div v-else class="step-rules-config__empty title">No rules added</div>
            </v-col>
        </v-row>

        <!-- Delete Confirmation Dialog -->
        <confirm-delete-dialog
            v-model="showDeleteDialog"
            message="Are you sure you want to delete this rule?"
            sub-message="This Rule will be permanently deleted. Transactions matched with this Rule will become unmatched."
            dat-testid="step-rules-config"
            @confirm="confirmDelete"
        />
    </div>
</template>

<script>
import ConfirmDeleteDialog from './ConfirmDeleteDialog.vue';

export default {
    name: 'StepMatchingRoutinesV2',

    components: { ConfirmDeleteDialog },

    props: {
        filenames: {
            type: Array,
            default: () => [],
        },
        columns: {
            type: Object,
            default: () => ({}),
        },
        rulesdata: {
            type: Array,
            default: () => [],
        },
        fileMapshow: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        filenamesLabel() {
            const names = (this.filenames || []).filter(Boolean);
            return names.length ? names.join('  ↔  ') : '';
        },
        sortedRulesData() {
            return [...this.rulesdata]
                .map((item, originalIndex) => ({ ...item, _originalIndex: originalIndex }))
                .sort((a, b) => Number(a.number) - Number(b.number));
        },
    },

    data() {
        return {
            draggedItemIndex: null,
            showDeleteDialog: false,
            deleteTargetIndex: null,
            isDirty: false,
        };
    },

    beforeDestroy() {
        this.draggedItemIndex = null;
        this.showDeleteDialog = false;
        this.deleteTargetIndex = null;
        this.isDirty = false;
    },

    methods: {
        hasValidData(data) {
            return Array.isArray(data) && data.length > 0;
        },
        promptDelete(index) {
            this.deleteTargetIndex = index;
            this.showDeleteDialog = true;
        },
        confirmDelete() {
            if (this.deleteTargetIndex !== null) {
                this.$emit('deleteRule', this.deleteTargetIndex);
                this.deleteTargetIndex = null;
                this.showDeleteDialog = false;
                // Parent's deleteRuleV2 handles the API call; no need to trigger saveOrder here.
            }
        },
        saveOrder() {
            this.isDirty = false;
            this.$emit('saveRuleOrderV2');
        },
        onDragStart(index) {
            this.draggedItemIndex = index;
        },
        onDroprule(index) {
            this.$emit('reorder-rules', {
                from: this.draggedItemIndex,
                to: index,
            });
            this.isDirty = true;
            this.draggedItemIndex = null;
        },
        onDragEnd() {
            this.draggedItemIndex = null;
        },
    },
};
</script>

<style lang="scss" scoped>
@import './StepMatchingRoutinesV2.scss';
</style>
